import React from "react"
import { Button, Flex, Text } from "@capioweb/ui-components/src"
import { ContentAreaProperty } from "../../@types/CapioWeb/core"
import {
	ContentReferenceProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { BlockResolver, BreadcrumbsBlock } from "../../components"
import { BreadcrumbItemProps } from "../../components/Core"
import { EpiContext, LocalesContext } from "../../context"
import { assetBaseUrl, shouldRender } from "../../utils"
import { INNER_MAX_WIDTH } from "@capioweb/ui-components/src/constants"
import { useLocation } from "react-router-dom"
import { scrollToAnchor } from "../../utils/scrollToAnchor"

interface PressAssetsDownloadPageProps {
	heading: StringProperty
	photographer: StringProperty
	reference: StringProperty
	imageSize: StringProperty
	fileType: StringProperty
	fileTypeText: StringProperty
	breadcrumb: Array<BreadcrumbItemProps>
	usageRights: StringProperty
	mediaFile: ContentReferenceProperty
	fileName: StringProperty
	license: StringProperty
	mainContentArea: ContentAreaProperty
	publishedDate: StringProperty
	expirationDate: StringProperty
	previewImage: StringProperty
	alternateText: StringProperty
}

const MetaInformationRow = (label: string, value: string) => {
	return (
		<Flex mb={1}>
			<Text fontWeight="bold" mr={1} mb={0}>
				{label}:
			</Text>
			<Text mb={0}>{value}</Text>
		</Flex>
	)
}

const PressAssetsDownloadPage: React.FC<PressAssetsDownloadPageProps> = (
	props
) => {
	const { editMode } = React.useContext(EpiContext)
	const { translate, translationKeys } = React.useContext(LocalesContext)

	const { hash } = useLocation()

	const id = decodeURI(hash.substring(1))

	const el = document?.getElementById(id)

	React.useEffect(() => {
		if (el) {
			//Scroll to anchorID position if it exists
			scrollToAnchor(el)
		}
	}, [el]) //UseEffect will run again when 'el' is ready

	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={...props.breadcrumb} />
			<Flex as="section" w={1} px={3} flexDirection="column">
				<Flex w={1} maxw={INNER_MAX_WIDTH} m="0 auto">
					<Flex maxw={93} mb={5} flexDirection="column">
						<Flex w={1} mb={4} flexGrow={1}>
							{props.mediaFile?.value?.url && (
								<img
									src={
										assetBaseUrl() +
										props.mediaFile.value.url +
										"?w=800&scale=down"
									}
									alt={props.alternateText?.value}
									width="100%"
								></img>
							)}
						</Flex>
						<Text variant="headline-1">
							{props.heading?.value !== ""
								? props.heading.value
								: props.fileName?.value}
						</Text>
						<Flex mt={2}>
							{props.fileTypeText?.value && (
								<Text
									variant="headline-4"
									as="p"
									textTransform="uppercase"
									mb={{ _: 0, xs: 3 }}
									lineHeight={3}
								>
									{props.fileTypeText.value}
								</Text>
							)}
							{/* Vertical line between fileTyoeText and date, only visible if both has values */}
							{props.fileTypeText?.value && props.publishedDate?.value && (
								<Flex
									mx={{ _: 1, xs: 2 }}
									w="1px"
									h={3}
									backgroundColor="blue33"
								></Flex>
							)}
							{props.publishedDate?.value && (
								<Text variant="paragraph-16">{props.publishedDate.value}</Text>
							)}
						</Flex>

						{props.fileType?.value &&
							MetaInformationRow(
								translate(translationKeys.fileFormat),
								props.fileType.value
							)}
						{props.imageSize?.value &&
							MetaInformationRow(
								translate(translationKeys.imageSize),
								props.imageSize.value
							)}
						{props.license?.value &&
							MetaInformationRow(
								translate(translationKeys.licence),
								props.license.value
							)}
						{props.usageRights?.value &&
							MetaInformationRow(
								translate(translationKeys.usageRights),
								props.usageRights.value
							)}
						{props.photographer?.value &&
							MetaInformationRow(
								translate(translationKeys.photographer),
								props.photographer.value
							)}
						{props.reference?.value &&
							MetaInformationRow(
								translate(translationKeys.reference),
								props.reference.value
							)}
						{props.publishedDate?.value &&
							MetaInformationRow(
								translate(translationKeys.published),
								props.publishedDate.value
							)}
						{props.expirationDate?.value &&
							MetaInformationRow(
								translate(translationKeys.stopPublish),
								props.expirationDate.value
							)}

						<Flex my={4} as="a" href={props.mediaFile.value.url ?? ""} download>
							<Button icon="arrow-down">
								{translate(translationKeys.download)}
							</Button>
						</Flex>
					</Flex>
				</Flex>
				{shouldRender(editMode.inEditMode, props.mainContentArea?.value) && (
					<div data-epi-edit="mainContentArea">
						<BlockResolver blocks={props.mainContentArea?.value} />
					</div>
				)}
			</Flex>
		</Flex>
	)
}

export { PressAssetsDownloadPage }
